import React, { useState } from 'react';
import { X, Minus, Plus } from 'lucide-react';
import { ptsFor } from '../utils/points';

const CompleteChoreModal = ({ chore, roommates, currentUser, onClose, onSubmit }) => {
  const [actualMinutes, setActualMinutes] = useState(chore?.defaultMinutes || 10);
  const [actualDifficulty, setActualDifficulty] = useState(chore?.defaultDifficulty || 2);
  const [additionalUserNote, setAdditionalUserNote] = useState('');
  const [helperIds, setHelperIds] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [doneAt, setDoneAt] = useState(() => {
    const now = new Date();
    // Offset correction to get local ISO string
    const localNow = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
    return localNow.toISOString().slice(0, 16);
  }); // YYYY-MM-DDTHH:mm

  if (!chore) return null;

  const pts = ptsFor(actualMinutes, actualDifficulty);

  const handleHelperToggle = (id) => {
    setHelperIds(prev =>
      prev.includes(id) ? prev.filter(hId => hId !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      actualMinutes: parseInt(actualMinutes),
      actualDifficulty: parseInt(actualDifficulty),
      additionalUserNote,
      helperIds,
      doneAt: new Date(doneAt).toISOString()
    });
  };

  return (
    <div className="fixed inset-0 bg-ink-overlay/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-ink-card2 rounded-t-3xl md:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col border border-white/10 transform transition-transform duration-300 translate-y-0">

        {/* Header */}
        <div className="flex justify-between items-center p-5 md:p-6 border-b border-white/10 bg-ink-card2 sticky top-0 z-10">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: currentUser?.color || 'oklch(0.72 0.155 48)' }} />
            <h2 className="text-xl font-serif text-ink-fg truncate">{chore.title}</h2>
          </div>
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <span className="px-2.5 py-1.5 rounded-lg bg-mint/15 text-mint text-[11px] font-mono font-semibold">+{pts} PTS</span>
            <button onClick={onClose} className="text-ink-fg/40 hover:text-ink-fg/70 bg-ink-card3 hover:bg-ink-card rounded-full p-2 transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 md:p-6 space-y-6 overflow-y-auto max-h-[85vh] custom-scrollbar">

          <p className="text-[10.5px] font-mono text-ink-fg/40 -mt-2">{actualMinutes} MIN × DIFFICULTY {actualDifficulty}</p>

          <div className="bg-ink-card3 p-4 rounded-2xl border border-white/10 space-y-5">
            <div>
              <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">Time Spent</label>
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-serif text-ink-fg">{actualMinutes}</span>
                  <span className="text-xs font-mono text-ink-fg/40 uppercase">min</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActualMinutes(m => Math.max(1, parseInt(m || 0) - 5))}
                    className="w-9 h-9 rounded-xl bg-ink-card2 border border-white/10 text-ink-fg/70 flex items-center justify-center hover:bg-ink-card hover:text-mint transition-colors active:scale-90"
                  >
                    <Minus size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActualMinutes(m => parseInt(m || 0) + 5)}
                    className="w-9 h-9 rounded-xl bg-ink-card2 border border-white/10 text-ink-fg/70 flex items-center justify-center hover:bg-ink-card hover:text-mint transition-colors active:scale-90"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">How hard was it</label>
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setActualDifficulty(n)}
                    className={`flex-1 h-9 rounded-xl text-sm font-bold transition-all ${n <= actualDifficulty
                      ? 'bg-mint text-ink-shell'
                      : 'bg-ink-card2 text-ink-fg/35 border border-white/10 hover:border-mint/25'
                      }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">When</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowDatePicker(false)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${!showDatePicker ? 'bg-mint text-ink-shell' : 'bg-ink-card3 text-ink-fg/60 border border-white/10 hover:border-mint/25'}`}
              >
                Now
              </button>
              <button
                type="button"
                onClick={() => setShowDatePicker(true)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${showDatePicker ? 'bg-mint text-ink-shell' : 'bg-ink-card3 text-ink-fg/60 border border-white/10 hover:border-mint/25'}`}
              >
                Pick…
              </button>
            </div>
            {showDatePicker && (
              <input
                type="datetime-local"
                value={doneAt}
                onChange={e => setDoneAt(e.target.value)}
                className="w-full mt-3 bg-ink-card3 border border-white/10 text-ink-fg text-sm rounded-xl px-4 py-3 focus:ring-2 focus:ring-mint outline-none transition-all animate-in slide-in-from-top-2 duration-200"
              />
            )}
          </div>

          {roommates.length > 0 && (
            <div>
              <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">Anyone help?</label>
              <div className="flex flex-wrap gap-2">
                {roommates.map(rm => {
                  const selected = helperIds.includes(rm.id);
                  return (
                    <button
                      key={rm.id}
                      type="button"
                      onClick={() => handleHelperToggle(rm.id)}
                      className={`flex items-center gap-2 pl-2.5 pr-3.5 py-2 rounded-xl text-sm font-medium transition-all ${selected
                        ? 'bg-ink-card border-2 shadow-sm'
                        : 'bg-ink-card3 border-2 border-transparent text-ink-fg/50 hover:text-ink-fg/80'
                        }`}
                      style={selected ? { borderColor: rm.color || 'oklch(0.72 0.155 48)', color: '#F4F1E9' } : undefined}
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: rm.color || 'oklch(0.72 0.155 48)' }} />
                      {rm.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <textarea
            value={additionalUserNote}
            onChange={e => setAdditionalUserNote(e.target.value)}
            className="w-full bg-ink-card3 border border-white/10 text-ink-fg rounded-xl px-4 py-3 focus:ring-2 focus:ring-mint outline-none transition-all resize-none placeholder:text-ink-fg/30"
            rows="2"
            placeholder="Add a note (optional)"
          />

          <div className="flex gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-12 rounded-2xl border border-white/15 text-ink-fg font-semibold text-sm hover:bg-ink-card3 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 h-12 rounded-2xl bg-mint text-ink-shell font-bold text-sm hover:brightness-95 transition-all active:scale-95"
            >
              Log it +{pts}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteChoreModal;
